import {
  Button,
  DatePicker,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteTwoTone, EditTwoTone, FilePdfTwoTone } from "@ant-design/icons";
import React, { useCallback } from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  deleteVacancyById,
  getVacancyList,
  selectVacancies,
  toggleVacancyById,
} from "../../store/slices/vacancySlice";
import AddVacancyPopup from "./AddVacancyPopup";
import { config } from "../../config";
import Link from "antd/lib/typography/Link";
import { formatDate } from "../../utils/date";

const { Search } = Input;
const { Text } = Typography;

const Vacancy = () => {
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeItem, setActiveitem] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    pageSizeOptions: ["10", "20", "50"],
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const vacancyList = useSelector(selectVacancies);
  const dispatch = useDispatch();

  const fetchData = (pagination, searchTerm, startDate, endDate) => {
    setLoading(true);
    dispatch(
      getVacancyList(
        startDate?.format("YYYY-MM-DD 00:00:00"),
        endDate?.format("YYYY-MM-DD 23:59:00"),
        searchTerm ?? "",
        pagination.current,
        pagination.pageSize,
        (totalData) => {
          setPagination({ ...pagination, total: totalData });
          setLoading(false);
        }
      )
    );
  };

  const refetchData = useCallback(() => {
    fetchData(pagination, searchTerm, startDate, endDate);
  }, [pagination, searchTerm, startDate, endDate]);

  useEffect(() => {
    fetchData(pagination, searchTerm);
  }, []);

  const handleTableChange = (pagination) => {
    fetchData(pagination, searchTerm, startDate, endDate);
  };

  const handleDelete = (item) => {
    setConfirmLoading(true);
    dispatch(
      deleteVacancyById(item.id, () => {
        setConfirmLoading(false);
      })
    );
  };

  const changeStatus = (item) => {
    setConfirmLoading(true);
    dispatch(
      toggleVacancyById(item.id, () => {
        setConfirmLoading(false);
      })
    );
  };

  const columns = [
    {
      title: "Created Date",
      dataIndex: "dates",
      render: (_, record) => {
        return <p> {new Date(_.created_at).toLocaleDateString()}</p>;
      },
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    // {
    //   title: "Location",
    //   dataIndex: "location",
    // },
    // {
    //   title: "Note",
    //   dataIndex: "note",
    // },
    // {
    //   title: "Required Documents",
    //   dataIndex: "requiredDocument",
    // },
    {
      title: "Deadline",
      dataIndex: "deadline",
      render: (_, record) => {
        return <Text>{formatDate(_)}</Text>;
      },
      width: "150px",
    },
    {
      title: "First Published Date",
      dataIndex: "firstPublishedDate",
      render: (_, record) => {
        return <Text>{formatDate(_)}</Text>;
      },
      width: "150px",
    },
    {
      title: "File",
      dataIndex: "filePath",
      render: (_, record) => {
        return (
          <FilePdfTwoTone
            title="Open file"
            onClick={() => {
              window.open(`${config.filesBaseUrl}/${_}`, "_blank");
            }}
            style={{
              fontSize: "30px",
              cursor: "pointer",
            }}
          />
        );
      },
    },
    {
      title: "Form Link",
      dataIndex: "applyUrl",
      render: (_, record) => {
        return (
          <Link target="_blank" href={_}>
            Open form
          </Link>
        );
      },
      width: "150px",
    },
    {
      title: "Published",
      dataIndex: "isPublished",
      render: (_, record) => {
        return (
          <>
            <Space>
              {_ ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}
              <Popconfirm
                title={`Are you sure want to ${
                  _ ? "unpublish" : "publish"
                } this vacancy`}
                onConfirm={() => changeStatus(record)}
              >
                <Button style={{ width: "150px" }} type="default">
                  {_ ? "Unpublish" : "Publish now"}
                </Button>
              </Popconfirm>
            </Space>
          </>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "isPublished",
      width: "100px",
      render: (_, record) => {
        return (
          <Space>
            <EditTwoTone
              title="Edit"
              twoToneColor="#1890ff"
              style={{
                fontSize: "20px",
                cursor: "pointer",
                marginRight: "8px",
              }}
              onClick={() => {
                setActiveitem(record);
                setIsModalVisible(true);
              }}
            />

            <Popconfirm
              confirmLoading={confirmLoading}
              title="Are you sure want to delete this vacancy?"
              onConfirm={() => handleDelete(record)}
            >
              <DeleteTwoTone
                title="Delete"
                twoToneColor="#ff0000"
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <h2 style={{ fontWeight: "bold", marginBottom: "20px" }}>Vacancies</h2>
      {isModalVisible && (
        <AddVacancyPopup
          closePopup={setIsModalVisible}
          item={activeItem}
          onUpdateSuccess={refetchData}
        />
      )}
      <Space
        style={{ width: "100%", marginBottom: "15px" }}
        size={10}
        direction="horizontal"
        align="end"
      >
        <Space direction="vertical">
          <Text>Start Date:</Text>
          <DatePicker
            format="MM/DD/YYYY"
            value={startDate}
            placeholder="Select start date"
            onChange={(value) => {
              setStartDate(value);
              fetchData(pagination, searchTerm, value, endDate);
            }}
            disabledDate={(current) => {
              return moment().add(0, "days") < current;
            }}
          />
        </Space>
        <Space direction="vertical">
          <Text>End Date:</Text>
          <DatePicker
            format="MM/DD/YYYY"
            defaultValue={startDate}
            placeholder="Select end date"
            onChange={(value) => {
              setEndDate(value);
              fetchData(pagination, searchTerm, startDate, value);
            }}
            disabledDate={(current) => {
              return moment().add(0, "days") < current || startDate > current;
            }}
          />
        </Space>
        <Space direction="vertical">
          <Text>Search(Title):</Text>
          <Search
            allowClear
            placeholder="Search"
            onSearch={(value) => {
              setSearchTerm(value);
              fetchData(pagination, value, startDate, endDate);
            }}
            enterButton
            style={{ width: "350px" }}
          />
        </Space>
        <Button
          type="primary"
          onClick={() => {
            setIsModalVisible(true);
            setActiveitem(null);
          }}
        >
          Add Vacancy
        </Button>
      </Space>
      <Table
        dataSource={vacancyList}
        columns={columns}
        loading={loading}
        bordered
        rowKey="id"
        pagination={pagination}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default Vacancy;
